<template>
    <div class="c-price-offer-modal" v-if="isOpen">
        <div class="c-price-offer-modal__backdrop" @click="close"></div>

        <div class="c-price-offer-modal__dialog" role="dialog" aria-modal="true">
            <button type="button" class="c-price-offer-modal__close" :aria-label="$t('PriceOffer.Modal.close')" @click="close">
                <i class="ico ico-close" aria-hidden="true"></i>
            </button>

            <div class="c-price-offer-modal__header">
                <div class="c-price-offer-modal__title">
                    {{ $t('PriceOffer.Modal.title') }}
                </div>

                <div class="c-price-offer-modal__subtitle">
                    {{ $t('PriceOffer.Modal.subtitle') }}
                </div>
            </div>

            <div class="c-price-offer-modal__content">
                <div class="c-price-offer-modal__search">
                    <div class="c-price-offer-modal__search-input">
                        <i class="ico ico-search" aria-hidden="true"></i>

                        <input
                            type="text"
                            class="c-form__input"
                            :placeholder="$t('PriceOffer.Modal.searchPlaceholder')"
                            v-model="search"
                        >
                    </div>

                    <button
                        type="button"
                        class="c-btn c-btn--ghost c-price-offer-modal__create-trigger"
                        :class="{'is-active': isCreateFormVisible}"
                        @click="toggleCreateForm"
                    >
                        {{ $t('PriceOffer.Modal.createTrigger') }}
                    </button>
                </div>

                <form class="c-price-offer-modal__create" v-if="isCreateFormVisible" @submit.prevent="createOffer">
                    <div class="c-price-offer-modal__create-item is-number">
                        <label class="c-price-offer-modal__label" for="priceOfferNumber">
                            {{ $t('PriceOffer.Modal.numberLabel') }}
                        </label>

                        <input
                            type="text"
                            id="priceOfferNumber"
                            class="c-form__input"
                            :value="nextNumber"
                            :title="$t('PriceOffer.Modal.numberHint')"
                            disabled
                        >
                    </div>

                    <div class="c-price-offer-modal__create-item is-name">
                        <label class="c-price-offer-modal__label" for="priceOfferName">
                            {{ $t('PriceOffer.Modal.nameLabel') }}
                        </label>

                        <input
                            type="text"
                            id="priceOfferName"
                            class="c-form__input"
                            :class="{'error': createError}"
                            :placeholder="$t('PriceOffer.Modal.namePlaceholder')"
                            maxlength="255"
                            ref="nameInput"
                            v-model="newName"
                        >
                    </div>

                    <button
                        type="submit"
                        class="c-btn c-price-offer-modal__create-submit"
                        :class="{'is-loading': isCreating}"
                        :disabled="isCreating"
                    >
                        {{ $t('PriceOffer.Modal.createSubmit') }}
                    </button>

                    <div class="c-price-offer-modal__error" v-if="createError">
                        {{ createError }}
                    </div>
                </form>

                <div
                    class="c-price-offer-modal__list is-skeleton"
                    v-if="isLoading"
                    aria-busy="true"
                    :aria-label="$t('PriceOffer.Modal.loading')"
                >
                    <div class="c-price-offer-modal__row" v-for="n in skeletonRows" :key="`skeleton-${n}`" aria-hidden="true">
                        <span class="c-price-offer-modal__box"></span>

                        <span class="c-price-offer-modal__row-offer">
                            <span class="c-price-offer-modal__line is-number"></span>
                            <span class="c-price-offer-modal__line is-name"></span>
                        </span>

                        <span class="c-price-offer-modal__line is-date"></span>
                    </div>
                </div>

                <div class="c-price-offer-modal__list" v-else-if="filteredOffers.length">
                    <label
                        class="c-price-offer-modal__row"
                        :class="{'is-selected': isSelected(offer.id)}"
                        v-for="offer in filteredOffers"
                        :key="offer.id"
                    >
                        <input
                            type="checkbox"
                            class="c-price-offer-modal__checkbox"
                            :value="offer.id"
                            :checked="isSelected(offer.id)"
                            @change="toggle(offer.id)"
                        >

                        <span class="c-price-offer-modal__box">
                            <i class="ico ico-tick" aria-hidden="true"></i>
                        </span>

                        <span class="c-price-offer-modal__row-offer">
                            <span class="c-price-offer-modal__row-number">{{ offer.number }}</span>
                            <span class="c-price-offer-modal__row-name">{{ offer.name }}</span>
                        </span>

                        <span class="c-price-offer-modal__row-customer">
                            {{ offer.customer_name }}
                        </span>

                        <span class="c-price-offer-modal__row-date">{{ offer.created }}</span>
                    </label>
                </div>

                <div class="c-price-offer-modal__list" v-else>
                    <div class="c-price-offer-modal__empty">
                        {{ search ? $t('PriceOffer.Modal.noResults') : $t('PriceOffer.Modal.empty') }}
                    </div>
                </div>
            </div>

            <div class="c-price-offer-modal__footer">
                <button type="button" class="c-btn c-btn--ghost" @click="close">
                    {{ $t('PriceOffer.Modal.cancel') }}
                </button>

                <button
                    type="button"
                    class="c-btn c-price-offer-modal__submit"
                    :class="{'is-loading': isSubmitting}"
                    :disabled="isSubmitting || !hasChanges"
                    @click="submit"
                >
                    <i class="ico ico-plus is-on-left" aria-hidden="true"></i>
                    <span>{{ $t('PriceOffer.Modal.submit') }}</span>
                </button>
            </div>
        </div>
    </div>
</template>

<script>
import client from "./../../../../../vendor/rshop/frontend/assets/js/common/client";

export default {
    props: {
        productId: {
            type: Number,
            required: true
        }
    },
    data() {
        return {
            skeletonRows: 5,
            isOpen: false,
            isLoading: false,
            isCreating: false,
            isSubmitting: false,
            isCreateFormVisible: false,
            offers: [],
            selectedIds: [],
            presentIds: [],
            nextNumber: '',
            newName: '',
            createError: '',
            search: ''
        }
    },
    computed: {
        filteredOffers() {
            const needle = this.search.trim().toLowerCase();

            if (!needle) {
                return this.offers;
            }

            return this.offers.filter(offer => {
                return `${offer.number} ${offer.name}`.toLowerCase().indexOf(needle) !== -1;
            });
        },
        addedIds() {
            return this.selectedIds.filter(id => this.presentIds.indexOf(id) === -1);
        },
        removedIds() {
            return this.presentIds.filter(id => this.selectedIds.indexOf(id) === -1);
        },
        hasChanges() {
            return this.addedIds.length > 0 || this.removedIds.length > 0;
        }
    },
    created() {
        rEvent.on('PriceOffer.open', () => this.open());

        document.addEventListener('keydown', this.onKeyDown);
    },
    beforeDestroy() {
        document.removeEventListener('keydown', this.onKeyDown);
    },
    methods: {
        open() {
            this.isOpen = true;
            document.body.classList.add('is-price-offer-modal-open');

            this.fetchOffers();
        },
        close() {
            this.isOpen = false;
            this.isCreateFormVisible = false;
            this.search = '';
            this.newName = '';
            this.createError = '';

            document.body.classList.remove('is-price-offer-modal-open');
        },
        onKeyDown(event) {
            if (this.isOpen && event.key === 'Escape') {
                this.close();
            }
        },
        isSelected(id) {
            return this.selectedIds.indexOf(id) !== -1;
        },
        toggle(id) {
            const index = this.selectedIds.indexOf(id);

            if (index === -1) {
                this.selectedIds.push(id);
            } else {
                this.selectedIds.splice(index, 1);
            }
        },
        fetchOffers() {
            this.isLoading = true;

            client
                .get('/cenove-ponuky/zoznam', { params: { id: this.productId } })
                .then(response => {
                    if (!response.data.status) {
                        return;
                    }

                    this.offers = response.data.data;
                    this.nextNumber = response.data.next_number;
                    this.presentIds = this.offers.filter(offer => offer.is_present).map(offer => offer.id);
                    this.selectedIds = this.presentIds.slice();
                })
                .catch(() => {
                    Eshop.alert(this.$t('PriceOffer.Modal.loadError'), { type: 'error' });
                })
                .then(() => {
                    this.isLoading = false;
                });
        },
        toggleCreateForm() {
            this.isCreateFormVisible = !this.isCreateFormVisible;
            this.createError = '';

            if (this.isCreateFormVisible) {
                this.$nextTick(() => this.$refs.nameInput.focus());
            }
        },
        createOffer() {
            if (this.isCreating) {
                return;
            }

            const name = this.newName.trim();

            if (!name) {
                this.createError = this.$t('PriceOffer.Modal.nameRequired');

                return;
            }

            this.isCreating = true;
            this.createError = '';

            client
                .post('/cenove-ponuky/vytvorit', { name: name })
                .then(response => {
                    if (!response.data.status) {
                        this.createError = response.data.message || this.$t('GeneralError');

                        return;
                    }

                    this.offers.unshift(response.data.offer);
                    this.selectedIds.push(response.data.offer.id);
                    this.nextNumber = response.data.next_number;

                    this.isCreateFormVisible = false;
                    this.newName = '';
                    this.search = '';
                })
                .catch(() => {
                    this.createError = this.$t('GeneralError');
                })
                .then(() => {
                    this.isCreating = false;
                });
        },
        submit() {
            if (this.isSubmitting || !this.hasChanges) {
                return;
            }

            this.isSubmitting = true;

            const quantity = this.getQuantity(),
                requests = this.addedIds
                    .map(id => client.post(`/cenove-ponuky/pridat-produkt/${id}`, {
                        product_id: this.productId,
                        quantity: quantity
                    }))
                    .concat(this.removedIds.map(id => client.post(`/cenove-ponuky/zmazat-produkt/${id}`, {
                        product_id: this.productId
                    })));

            Promise
                .all(requests)
                .then(responses => {
                    const failed = responses.filter(response => !response.data.status);

                    if (failed.length) {
                        Eshop.alert(failed[0].data.message || this.$t('GeneralError'), { type: 'error' });

                        return;
                    }

                    rEvent.dispatch('PriceOffer.productSaved', {
                        productId: this.productId,
                        addedIds: this.addedIds,
                        removedIds: this.removedIds
                    });

                    Eshop.alert(this.$t('PriceOffer.Modal.saved'), { type: 'success' });

                    this.close();
                })
                .catch(() => {
                    Eshop.alert(this.$t('GeneralError'), { type: 'error' });
                })
                .then(() => {
                    this.isSubmitting = false;
                });
        },
        getQuantity() {
            const input = document.querySelector('.c-product-info__cart .c-form__quantity input[type="text"], .c-product-info__cart input[name="quantity"]'),
                quantity = input ? parseFloat(input.value.replace(',', '.')) : 1;

            return quantity > 0 ? quantity : 1;
        }
    }
}
</script>
